import {
  GTG_TARGET_COUNTRY_ES,
  GTG_TARGET_COUNTRY_TH,
  normalizeGtgTargetCountry,
} from "@healthdesk/shared-utils";
import spainProvinceGeoContext from "../../data/spainProvinceGeoContext.json";
import thailandProvinceGeoContext from "../../data/thailandProvinceGeoContext.json";
import spainProvinceHealthContext from "../../data/spainProvinceHealthContext.json";
import thailandProvinceHealthContext from "../../data/thailandProvinceHealthContext.json";

/** @type {{ geometrySource: string, dataVintageNote: string, provinces: Record<string, { lat: number, lng: number, landAreaSqKm: number, matchedNaturalEarthName: string }> }} */
const SPAIN_GEO = spainProvinceGeoContext;

/** @type {typeof SPAIN_GEO} */
const THAILAND_GEO = thailandProvinceGeoContext;

/** @type {{ meta: Record<string, unknown>, provinces: Record<string, Record<string, unknown>> }} */
const SPAIN_HEALTH = spainProvinceHealthContext;

/** @type {{ meta: Record<string, unknown>, provinces: Record<string, Record<string, unknown>> }} */
const THAILAND_HEALTH = thailandProvinceHealthContext;

export const SPAIN_PROVINCE_GEO_SOURCE_NOTE = SPAIN_GEO.geometrySource;

export const THAILAND_PROVINCE_GEO_SOURCE_NOTE = THAILAND_GEO.geometrySource;

export const GTG_PROVINCE_GEO_VINTAGE_CAPTION =
  "Boundary / area context from Natural Earth admin-1 (see geometry source note on the map). Not a national statistics office official boundary.";

export const SPAIN_HEALTH_STATS_CAPTION = `Official demographics & mortality: Eurostat ${SPAIN_HEALTH.meta.eurostatDataset}, reference year ${SPAIN_HEALTH.meta.referencePeriod}. ${SPAIN_HEALTH.meta.licenseNote}`;

export const THAILAND_HEALTH_STATS_CAPTION = `Official census demographics: ${THAILAND_HEALTH.meta.population.source}, table ${THAILAND_HEALTH.meta.population.table}, census ${THAILAND_HEALTH.meta.population.censusYearGregorian}. ${THAILAND_HEALTH.meta.population.licenseNote}`;

/** Google Ads–aligned label (canonical key is already English per geo targets extract). */
export function getGtgProvinceDisplayName(canonicalName) {
  const n = typeof canonicalName === "string" ? canonicalName.trim() : "";
  return n;
}

/**
 * @param {string} targetCountry
 * @param {string} canonicalProvinceName
 * @returns {{ lat: number, lng: number, landAreaSqKm: number, matchedNaturalEarthName: string } | null}
 */
export function getGtgProvinceGeoRow(targetCountry, canonicalProvinceName) {
  const c = normalizeGtgTargetCountry(targetCountry);
  const name = typeof canonicalProvinceName === "string" ? canonicalProvinceName.trim() : "";
  if (!name) return null;
  if (c === GTG_TARGET_COUNTRY_ES) {
    const row = SPAIN_GEO.provinces[name];
    return row && typeof row === "object" ? { ...row } : null;
  }
  if (c === GTG_TARGET_COUNTRY_TH) {
    const row = THAILAND_GEO.provinces[name];
    return row && typeof row === "object" ? { ...row } : null;
  }
  return null;
}

/**
 * @param {string} targetCountry
 * @param {string} canonicalProvinceName
 * @returns {Record<string, unknown> | null}
 */
export function getGtgProvinceHealthRow(targetCountry, canonicalProvinceName) {
  const c = normalizeGtgTargetCountry(targetCountry);
  const name = typeof canonicalProvinceName === "string" ? canonicalProvinceName.trim() : "";
  if (!name) return null;
  if (c === GTG_TARGET_COUNTRY_ES) {
    const row = SPAIN_HEALTH.provinces[name];
    return row && typeof row === "object" ? { ...row } : null;
  }
  if (c === GTG_TARGET_COUNTRY_TH) {
    const row = THAILAND_HEALTH.provinces[name];
    return row && typeof row === "object" ? { ...row } : null;
  }
  return null;
}

/**
 * @param {string} targetCountry
 * @param {string} canonicalProvinceName
 * @returns {{ lat: number, lng: number } | null}
 */
export function getGtgProvinceCentroid(targetCountry, canonicalProvinceName) {
  const row = getGtgProvinceGeoRow(targetCountry, canonicalProvinceName);
  if (!row || typeof row.lat !== "number" || typeof row.lng !== "number") return null;
  return { lat: row.lat, lng: row.lng };
}

/**
 * Merged row for province targeting charts (numeric keys chart-safe).
 * @param {string} targetCountry
 * @param {string} canonicalProvinceName
 * @returns {Record<string, unknown> | null}
 */
export function getGtgProvinceTargetingSnapshot(targetCountry, canonicalProvinceName) {
  const c = normalizeGtgTargetCountry(targetCountry);
  const name = typeof canonicalProvinceName === "string" ? canonicalProvinceName.trim() : "";
  if (!name) return null;
  if (c !== GTG_TARGET_COUNTRY_ES && c !== GTG_TARGET_COUNTRY_TH) return null;
  const geo = getGtgProvinceGeoRow(targetCountry, canonicalProvinceName);
  const health = getGtgProvinceHealthRow(targetCountry, canonicalProvinceName);
  if (!geo && !health) return null;
  const base = {
    province: name,
    landAreaSqKm: geo?.landAreaSqKm ?? null,
    matchedNaturalEarthName: geo?.matchedNaturalEarthName ?? null,
    totalPopulation: null,
    crudeDeathRatePer1000: null,
    crudeBirthRatePer1000: null,
    deathsTotal: null,
    liveBirthsTotal: null,
    pctFemalePopulation: null,
  };
  if (c === GTG_TARGET_COUNTRY_ES && health) {
    return {
      ...base,
      totalPopulation: typeof health.totalPopulation === "number" ? health.totalPopulation : null,
      crudeDeathRatePer1000:
        typeof health.crudeDeathRatePer1000 === "number" ? health.crudeDeathRatePer1000 : null,
      crudeBirthRatePer1000:
        typeof health.crudeBirthRatePer1000 === "number" ? health.crudeBirthRatePer1000 : null,
      deathsTotal: typeof health.deathsTotal === "number" ? health.deathsTotal : null,
      liveBirthsTotal: typeof health.liveBirthsTotal === "number" ? health.liveBirthsTotal : null,
    };
  }
  if (c === GTG_TARGET_COUNTRY_TH && health) {
    return {
      ...base,
      totalPopulation: typeof health.totalPopulation === "number" ? health.totalPopulation : null,
      pctFemalePopulation:
        typeof health.pctFemalePopulation === "number" ? health.pctFemalePopulation : null,
    };
  }
  return base;
}

export const GTG_PROVINCE_GOOGLE_ADS_NOTE =
  "Province names match Google Ads geo targets (Active · Province · ES or TH) from the published geo targets CSV.";
